import clamp from 'lodash/clamp';

import {
	isSelectionAtEndOfNode,
	isSelectionAtStartOfNode,
} from '@atlaskit/editor-common/selection';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { Decoration, DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';

import { getPluginState } from '../../pm-plugins/decoration/decoration-plugin-factory';

import { SELECTION_CATEGORY } from './analytics/enums';
import type { NodeAndMarkCounts } from './analytics/enums';
import type { Positions } from './xstate/get-ai-experience-service';

function findFirstDeco(decorationSet: DecorationSet, key: string): Decoration | undefined {
	const found = decorationSet.find(undefined, undefined, (spec) => spec.key === key);
	return found.length > 0 ? found[0] : undefined;
}

export function getAIHighlightPositions({
	modalDecorationSet,
}: {
	modalDecorationSet?: DecorationSet;
}): [number, number] | undefined {
	if (!modalDecorationSet) {
		return undefined;
	}

	const inlineDecoration = findFirstDeco(modalDecorationSet, 'inlineDecoration');
	if (inlineDecoration) {
		return [inlineDecoration.from, inlineDecoration.to];
	}

	const startWidget = findFirstDeco(modalDecorationSet, 'generatedStartWidget');
	const endWidget = findFirstDeco(modalDecorationSet, 'generatedEndWidget');

	if (startWidget && endWidget) {
		return [startWidget.from, endWidget.to];
	}

	return undefined;
}

/**
 * Determines the selection category based on the editor state and positions.
 *
 * @param {EditorView} editorView - The editor state.
 * @param {Positions} positions - The positions of the selection.
 * @returns {SELECTION_CATEGORY} The selection category.
 *
 * @example
 * const state = editorView.state;
 * const positions = [10, 20];
 * const category = getSelectionCategoryUsingState(state, positions);
 */
export function getSelectionCategory(
	editorView: EditorView,
	positions: Positions,
): SELECTION_CATEGORY {
	const { state } = editorView;
	return getSelectionCategoryUsingState(state, positions);
}

/**
 * Determines the selection category based on the editor state and positions.
 *
 * @param {EditorState} state - The editor state.
 * @param {Positions} positions - The positions of the selection.
 * @returns {SELECTION_CATEGORY} The selection category.
 *
 * @example
 * const state = editorView.state;
 * const positions = [10, 20];
 * const category = getSelectionCategoryUsingState(state, positions);
 */
export function getSelectionCategoryUsingState(
	state: EditorState,
	positions: Positions,
): SELECTION_CATEGORY {
	const [start, end] = positions;

	const { isInDocumentStreamingEnabled } = getPluginState(state);

	const isEmptySelection = start === end;
	// For in-document streaming, somehow during streaming, the position can be larger than the document size
	// So we clamp the position to the document size to quickly bypass the issue
	// In the future, when we refactor the decoration plugin, we should revisit this if needed
	const $start = isInDocumentStreamingEnabled
		? state.doc.resolve(clamp(start, 1, state.doc.content.size))
		: state.doc.resolve(start);
	const $end = isInDocumentStreamingEnabled
		? state.doc.resolve(clamp(end, 1, state.doc.content.size))
		: state.doc.resolve(end);

	const isSameParent = $start.sameParent($end);
	const isBeginningOfDoc = $start.pos === $start.depth;
	const isEndOfDoc = state.doc.nodeSize - $end.pos - 2 === $end.depth;
	const isStartOfParentNode = isSelectionAtStartOfNode($start, $start.parent);
	const isEndOfParentNode = isSelectionAtEndOfNode($end, $end.parent);

	if (isEmptySelection) {
		return SELECTION_CATEGORY.EMPTY;
	}
	if (isBeginningOfDoc && isEndOfDoc) {
		return SELECTION_CATEGORY.FULL_DOCUMENT;
	}
	if (isSameParent) {
		if (isStartOfParentNode && isEndOfParentNode) {
			return SELECTION_CATEGORY.FULL_SINGLE_BLOCK;
		}
		return SELECTION_CATEGORY.PARTIAL_SINGLE_BLOCK;
	}
	if (isStartOfParentNode && isEndOfParentNode) {
		return SELECTION_CATEGORY.FULL_MULTIPLE_BLOCKS;
	}
	return SELECTION_CATEGORY.PARTIAL_MULTIPLE_BLOCKS;
}

export function hasTextualContent(state: EditorState, positions: Positions): boolean {
	const [start, end] = positions;
	const text = state.doc.textBetween(start, end);
	return text.trim().length > 0;
}

/**
 * Gets a map of node and mark type names and their count occurrences within the selection.
 *
 * This function traverses the selected content and counts each unique node and mark type
 * in the selection. This can be used for analytics to track what types of content
 * is being selected.
 *
 * @param {EditorState} state - The editor state.
 * @param {Positions} positions - The positions of the selection [start, end].
 * @returns {NodeAndMarkCounts} A map of node/mark type names to their counts.
 *
 * @example
 * const state = editorView.state;
 * const positions = [10, 20];
 * const nodeTypes = getSelectionNodeTypes(state, positions);
 * // Returns: { paragraph: 1, text: 5, strong: 2, link: 1 }
 */
export function getSelectionNodeTypes(state: EditorState, positions: Positions): NodeAndMarkCounts {
	const [start, end] = positions;
	const nodeCounts: NodeAndMarkCounts = {};

	// Walk through the document between the start and end positions
	state.doc.nodesBetween(start, end, (node) => {
		// Count the node type
		nodeCounts[node.type.name] = (nodeCounts[node.type.name] ?? 0) + 1;

		// Count each mark on the node
		node.marks.forEach((mark) => {
			nodeCounts[mark.type.name] = (nodeCounts[mark.type.name] ?? 0) + 1;
		});
	});

	return nodeCounts;
}
